import { z } from 'zod';
import { FetchClient, useFetchClient } from '@foundatiofx/fetchclient';
import { IsSecretRevealer } from './auth';

export const hubSpotConfigSchema = z.object({
    region: z.string(),
    portalId: z.string(),
    formId: z.string()
});

export const datadogRumConfigSchema = z.object({
    applicationId: z.string(),
    clientToken: z.string(),
    env: z.string().nullish(),
    sessionSampleRate: z.number(),
    traceSampleRate: z.number()
});

export const versionSchema = z.object({
    version: z.string(),
    environment: z.string(),
    hubSpot: hubSpotConfigSchema.optional(),
    datadogRum: datadogRumConfigSchema.nullable().optional()
});

export type HubSpotConfig = z.infer<typeof hubSpotConfigSchema>;
export type DatadogRumConfig = z.infer<typeof datadogRumConfigSchema>;

export const decryptedFieldSchema = z.object({
    decryptedValue: z.string()
});

export type Version = z.infer<typeof versionSchema>;
export type DecryptedField = z.infer<typeof decryptedFieldSchema>;

export interface ClientClockResult {
    clientId: number;
    utcNow: string;
    catchingUp: boolean;
}

export type TimeTravelJobMode = 'OnceAtDestination' | 'Never' | 'EachOccurrence';

export interface TimeTravelJob {
    /** Stable key that replay modes are sent under. */
    name: string;
    label: string;
    schedule: string;
    scheduleDescription: string;
    /** Fires more than once a day, so per-occurrence replay collapses to one run per date. */
    subDaily: boolean;
    /** Replayed at the end of each date instead of at its cron time of day. */
    runsAfterDailyJobs: boolean;
}

export class UtilitiesApi {
    constructor(
        private fetch = new FetchClient(),
        private route = 'utility'
    ) {}

    async getVersion() {
        return await useFetchClient().getJSON<Version>(`${this.route}/version`);
    }

    /**
     * Jumping forward replays the scheduled jobs the client skipped. Every job runs at `defaultMode` unless
     * `modes` names it, so only deviations need to be sent. The default also governs the client's own
     * scheduled scripts and imports, which have no catalog entry to name.
     */
    async setClientClock(
        clientId: number,
        utcIso: string,
        modes?: Record<string, TimeTravelJobMode>,
        defaultMode?: TimeTravelJobMode
    ) {
        return await useFetchClient().postJSON<ClientClockResult>(`${this.route}/clock/client/${clientId}/travel`, {
            time: utcIso,
            modes: modes ?? {},
            defaultMode
        });
    }

    async getTimeTravelJobs() {
        return await useFetchClient().getJSON<TimeTravelJob[]>(`${this.route}/clock/jobs`);
    }

    async clearClientClock(clientId: number) {
        return await useFetchClient().delete(`${this.route}/clock/client/${clientId}`, { expectedStatusCodes: [404] });
    }

    async getDecryptedField(entityType: string, entityId: number, key: string) {
        if (!IsSecretRevealer()) {
            throw new Error('User does not have permission to reveal secret fields.');
        }

        return await useFetchClient().getJSON<DecryptedField>(
            `/${entityType}s/${entityId}/properties/${key}/decrypted`
        );
    }
}
